import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import {
  DEFAULT_ACCESS,
  canUseAction,
  clearMenuAccessCache,
  fetchLoginUserMenu,
  getMenuAccess,
  routeToMenuPath
} from 'api/access';

const AccessControlContext = createContext({
  access: DEFAULT_ACCESS,
  menus: null,
  loading: true,
  menuPath: null,
  can: () => true
});

export function AccessControlProvider({ children }) {
  const location = useLocation();
  const [menus, setMenus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    function loadMenus(force = false) {
      setLoading(true);
      fetchLoginUserMenu({ force })
        .then((items) => {
          if (!active) return;
          setMenus(items);
          setError('');
        })
        .catch((err) => {
          if (!active) return;
          setMenus([]);
          setError(err.message || 'Unable to load permissions');
        })
        .finally(() => active && setLoading(false));
    }

    loadMenus();

    function handlePermissionUpdate() {
      clearMenuAccessCache();
      loadMenus(true);
    }

    window.addEventListener('hrmsPermissionsUpdated', handlePermissionUpdate);
    window.addEventListener('storage', handlePermissionUpdate);

    return () => {
      active = false;
      window.removeEventListener('hrmsPermissionsUpdated', handlePermissionUpdate);
      window.removeEventListener('storage', handlePermissionUpdate);
    };
  }, []);

  const menuPath = useMemo(() => routeToMenuPath(location.pathname), [location.pathname]);
  const access = useMemo(() => menuPath ? getMenuAccess(menus || [], menuPath) : DEFAULT_ACCESS, [menus, menuPath]);

  const value = useMemo(() => ({
    access,
    menus,
    loading,
    error,
    menuPath,
    can: (action) => canUseAction(access, action)
  }), [access, error, loading, menuPath, menus]);

  return (
    <AccessControlContext.Provider value={value}>
      {children}
    </AccessControlContext.Provider>
  );
}

export function AccessViewGuard({ children }) {
  const { access, error, loading, menuPath, menus } = useAccessControl();

  if (loading && menus === null) return null;
  if (!menuPath) return children;

  if (error) {
    return <AccessState title="Unable to load permissions" message={error} />;
  }

  if (!access.view) {
    return <AccessState title="Access denied" message={`You do not have view access for ${menuPath}`} />;
  }

  return children;
}

export function useAccessControl() {
  return useContext(AccessControlContext);
}

function AccessState({ title, message }) {
  return (
    <Box sx={{ minHeight: 360, display: 'grid', placeItems: 'center', textAlign: 'center', px: 2 }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">{message}</Typography>
      </Box>
    </Box>
  );
}
